const DEFAULT_BAUD_RATE = 9600;
const WRITE_CHUNK_SIZE = 512;
const OPEN_RETRY_DELAY_MS = 350;
const BLUETOOTH_RECONNECT_DELAY_MS = 1000;

export class PrinterConnectionError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'PrinterConnectionError';
        this.code = code;
    }
}

export const getFriendlyPrinterError = (error) => {
    if (!error) return 'Ocurrió un problema al comunicarse con la impresora.';
    if (error instanceof PrinterConnectionError) return error.message;

    switch (error.name) {
        case 'NotFoundError':
            return 'No seleccionaste ninguna impresora. Puedes volver a intentarlo cuando estés listo.';
        case 'NotAllowedError':
        case 'SecurityError':
            return 'Chrome o Edge no tiene permiso para usar la impresora.';
        case 'InvalidStateError':
            return 'La impresora ya está en uso. Cierra otras ventanas que puedan estar utilizándola e intenta nuevamente.';
        case 'NetworkError':
            return 'No se pudo abrir la impresora. Verifica que esté encendida y conectada.';
        case 'UnknownError':
            return 'Windows perdio la comunicacion Bluetooth con la impresora. Espera unos segundos o selecciona nuevamente la TSC.';
        default:
            return error.message || 'Ocurrió un problema al comunicarse con la impresora.';
    }
};

const getPortInformation = (port) => {
    if (!port || typeof port.getInfo !== 'function') return {};
    return port.getInfo() || {};
};

const matchesPort = (port, fingerprint) => {
    if (!fingerprint) return true;
    const info = getPortInformation(port);
    const keys = ['usbVendorId', 'usbProductId', 'bluetoothServiceClassId'];
    const comparableKeys = keys.filter((key) => fingerprint[key] !== undefined);
    if (comparableKeys.length === 0) return true;
    return comparableKeys.every((key) => String(info[key]) === String(fingerprint[key]));
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const isRecoverableWriteError = (error) => {
    if (!error) return false;
    if (['UnknownError', 'NetworkError', 'InvalidStateError'].includes(error.name)) return true;
    return /unknown system error|device.*lost|disconnected|bluetooth/i.test(error.message || '');
};

export class WebSerialPrinter {
    constructor() {
        this.port = null;
        this.connectionListeners = new Set();
        this.serialEventsAttached = false;
    }

    isSupported() {
        return typeof navigator !== 'undefined' && Boolean(navigator.serial);
    }

    assertSupported() {
        if (!this.isSupported()) {
            throw new PrinterConnectionError(
                'Este navegador no permite Web Serial. Usa Chrome o Edge en Windows.',
                'WEB_SERIAL_UNSUPPORTED'
            );
        }
    }

    async selectPort() {
        this.assertSupported();
        await this.disconnect();
        this.port = await navigator.serial.requestPort();
        return getPortInformation(this.port);
    }

    isConnected() {
        return Boolean(this.port && this.port.writable);
    }

    notifyConnectionChange() {
        const connected = this.isConnected();
        this.connectionListeners.forEach((listener) => {
            try {
                listener(connected);
            } catch (error) {
                // Un listener visual no debe interrumpir la comunicacion serial.
            }
        });
    }

    handleSerialDisconnect = (event) => {
        const disconnectedPort = event && event.port;
        if (!disconnectedPort || disconnectedPort === this.port) {
            this.port = null;
            this.notifyConnectionChange();
        }
    };

    attachSerialEvents() {
        if (this.serialEventsAttached || !this.isSupported()) return;
        if (typeof navigator.serial.addEventListener !== 'function') return;
        navigator.serial.addEventListener('disconnect', this.handleSerialDisconnect);
        this.serialEventsAttached = true;
    }

    subscribeConnection(listener) {
        if (typeof listener !== 'function') return () => {};
        this.attachSerialEvents();
        this.connectionListeners.add(listener);
        listener(this.isConnected());
        return () => this.connectionListeners.delete(listener);
    }

    async findGrantedPorts(fingerprint, excludedPort = null) {
        this.assertSupported();
        const ports = await navigator.serial.getPorts();
        return ports.filter((port) => port !== excludedPort && matchesPort(port, fingerprint));
    }

    async openPort(port, configuration = {}) {
        let lastError = null;

        for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
                if (!port.writable) {
                    await port.open({
                        baudRate: Number(configuration.baudRate) || DEFAULT_BAUD_RATE,
                        dataBits: 8,
                        stopBits: 1,
                        parity: 'none',
                        flowControl: 'none'
                    });
                }

                if (!port.writable) {
                    throw new PrinterConnectionError(
                        'La impresora no tiene un canal de escritura disponible.',
                        'PORT_NOT_WRITABLE'
                    );
                }

                this.notifyConnectionChange();
                return getPortInformation(port);
            } catch (error) {
                lastError = error;
                if (error.name !== 'NetworkError' || attempt === 1) break;
                await wait(OPEN_RETRY_DELAY_MS);
            }
        }

        this.notifyConnectionChange();
        throw lastError;
    }

    selectSingleGrantedPort = async (fingerprint, excludedPort = null) => {
        const ports = await this.findGrantedPorts(fingerprint, excludedPort);
        if (ports.length > 1) {
            throw new PrinterConnectionError(
                'Hay varios puertos seriales Bluetooth autorizados y no es seguro elegir uno automaticamente. Abre Configurar impresora y selecciona nuevamente la TSC Alpha-3R.',
                'MULTIPLE_GRANTED_PORTS'
            );
        }
        return ports[0] || null;
    }

    async open(configuration = {}, allowSelection = false) {
        this.assertSupported();
        if (!this.port) {
            try {
                this.port = await this.selectSingleGrantedPort(configuration.portFingerprint);
            } catch (error) {
                if (!allowSelection || error.code !== 'MULTIPLE_GRANTED_PORTS') throw error;
            }
        }
        if (!this.port && allowSelection) {
            await this.selectPort();
        }
        if (!this.port) {
            throw new PrinterConnectionError(
                'No hay una impresora autorizada. Abre Configurar impresora y selecciona el dispositivo.',
                'PRINTER_PERMISSION_REQUIRED'
            );
        }

        const failedPort = this.port;
        try {
            return await this.openPort(this.port, configuration);
        } catch (firstError) {
            this.port = null;

            const replacementPort = await this.selectSingleGrantedPort(
                configuration.portFingerprint,
                failedPort
            );
            if (replacementPort) {
                this.port = replacementPort;
                try {
                    return await this.openPort(this.port, configuration);
                } catch (replacementError) {
                    this.port = null;
                    if (!allowSelection) throw replacementError;
                }
            } else if (!allowSelection) {
                throw firstError;
            }

            await this.selectPort();
            return this.openPort(this.port, configuration);
        }
    }

    async writeToCurrentPort(rawData) {
        if (!this.port.writable) {
            throw new PrinterConnectionError('La impresora no tiene un canal de escritura disponible.', 'PORT_NOT_WRITABLE');
        }

        const writer = this.port.writable.getWriter();
        const bytes = new TextEncoder().encode(rawData);
        try {
            for (let offset = 0; offset < bytes.length; offset += WRITE_CHUNK_SIZE) {
                await writer.write(bytes.slice(offset, offset + WRITE_CHUNK_SIZE));
            }
        } finally {
            writer.releaseLock();
        }
    }

    async write(rawData, configuration = {}, allowSelection = false) {
        await this.open(configuration, allowSelection);

        try {
            await this.writeToCurrentPort(rawData);
            this.notifyConnectionChange();
        } catch (firstError) {
            if (!isRecoverableWriteError(firstError)) throw firstError;

            await this.disconnect();
            await wait(BLUETOOTH_RECONNECT_DELAY_MS);

            try {
                // No abre el selector automaticamente durante una venta.
                await this.open(configuration, false);
                await this.writeToCurrentPort(rawData);
                this.notifyConnectionChange();
            } catch (retryError) {
                await this.disconnect();
                if (retryError instanceof PrinterConnectionError) throw retryError;
                throw new PrinterConnectionError(
                    'No se pudo restablecer la conexion Bluetooth. Abre Configurar impresora y selecciona nuevamente la TSC.',
                    'BLUETOOTH_RECONNECT_FAILED'
                );
            }
        }
    }

    async disconnect() {
        if (!this.port) return;
        const port = this.port;
        this.port = null;
        this.notifyConnectionChange();
        if (port.readable && port.readable.locked) return;
        if (port.writable && port.writable.locked) return;
        if (!port.readable && !port.writable) return;

        try {
            await port.close();
        } catch (error) {
            if (!['NetworkError', 'InvalidStateError', 'UnknownError'].includes(error.name)) throw error;
        }
    }
}

export default new WebSerialPrinter();
